import React, { useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend
);

type Props = {
  weeks: any;
  plannedWeekly: any[];
  actualWeekly: any[];
  cumulativePlanned: any[];
  cumulativeActual: any[];
};

const WeeklyProgressLineChart = ({
  weeks,
  plannedWeekly,
  actualWeekly,
  cumulativePlanned,
  cumulativeActual,
}: Props) => {
  const { t } = useTranslation();
  const { data, options } = useMemo(() => {
    const d = {
      labels: weeks,
      datasets: [
        {
          type: 'bar' as const,
          label: t('setup.label.주간 계획 진행률', '주간 계획 진행률') + ' (%)',
          data: plannedWeekly,
          backgroundColor: 'rgba(30,144,255,0.45)', // blue (planned)
          borderColor: 'rgba(30,144,255,1)',
          borderWidth: 1,
          yAxisID: 'y-left',
        },
        {
          type: 'bar' as const,
          label: t('setup.label.주간 실적 진행률', '주간 실적 진행률') + ' (%)',
          data: actualWeekly,
          backgroundColor: 'rgba(239,68,68,0.45)', // red (actual)
          borderColor: 'rgba(239,68,68,1)',
          borderWidth: 1,
          yAxisID: 'y-left',
        },
        {
          type: 'line' as const,
          label: t('setup.label.누적 계획 진행률', '누적 계획 진행률') + ' (%)',
          data: cumulativePlanned,
          borderColor: 'rgba(30,144,255,1)',
          backgroundColor: 'rgba(30,144,255,0.15)',
          yAxisID: 'y-right',
          tension: 0.25,
          pointRadius: 3,
        },
        {
          type: 'line' as const,
          label: t('setup.label.누적 실적 진행률', '누적 실적 진행률') + ' (%)',
          data: cumulativeActual,
          borderColor: 'rgba(239,68,68,1)',
          backgroundColor: 'rgba(239,68,68,0.12)',
          yAxisID: 'y-right',
          tension: 0.25,
          pointRadius: 3,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const defaults = ChartJS.defaults.plugins.legend.labels.generateLabels(chart) ?? [];
              return defaults.map((legendItem) => {
                const svgType = chart?.data?.datasets?.[legendItem.datasetIndex || 0]?.type;
                const svgColor = chart?.data?.datasets?.[legendItem.datasetIndex || 0]?.borderColor;
                if (svgType === 'line') {
                  const icon = getLegendIcon(svgColor);
                  if (icon) {
                    legendItem.pointStyle = icon;
                  }
                } else {
                  legendItem.pointStyle = 'rect';
                }
                return legendItem;
              });
            },
          },
        },
        tooltip: { enabled: true },
      },
      scales: {
        x: {
          display: true,
          ticks: { maxRotation: 0, autoSkip: false },
        },
        'y-left': {
          type: 'linear' as const,
          position: 'left' as const,
          suggestedMin: 0,
          suggestedMax: 10,
          title: { display: true, text: t('setup.label.주간 진행률 (%)', '주간 진행률 (%)') },
        },
        'y-right': {
          type: 'linear' as const,
          position: 'right' as const,
          suggestedMin: 0,
          suggestedMax: 100,
          grid: { drawOnChartArea: true },
          title: { display: true, text: t('setup.label.누적 진행률 (%)', '누적 진행률 (%)') },
        },
      },
    };

    return {
      data: d,
      options: opts,
      plannedWeekly,
      actualWeekly,
    };
  }, [weeks, plannedWeekly, actualWeekly]);

  const tableRows = useMemo(() => {
    return [
      {
        key: 'plannedWeekly',
        label: t('setup.label.주간 계획 진행률', '주간 계획 진행률'),
        color: 'rgba(30,144,255,0.85)',
        shape: 'bar',
        values: plannedWeekly,
      },
      {
        key: 'actualWeekly',
        label: t('setup.label.주간 실적 진행률', '주간 실적 진행률'),
        color: 'rgba(239,68,68,0.85)',
        shape: 'bar',
        values: actualWeekly,
      },
      {
        key: 'cumulativePlanned',
        label: t('setup.label.누적 계획 진행률', '누적 계획 진행률'),
        color: 'rgba(30,144,255,1)',
        shape: 'line',
        values: cumulativePlanned,
      },
      {
        key: 'cumulativeActual',
        label: t('setup.label.누적 실적 진행률', '누적 실적 진행률'),
        color: 'rgba(239,68,68,1)',
        shape: 'line',
        values: cumulativeActual,
      },
    ];
  }, [plannedWeekly, actualWeekly, cumulativePlanned, cumulativeActual, weeks]);

  const chartRef = useRef<InstanceType<typeof ChartJS> | null>(null);
  const [tableMargins, setTableMargins] = useState({ left: 0, right: 0, cellWidth: 50 });

  const getLegendIcon = (color: string) => {
    const img = new Image();
    const svgDataUrl = (svg: string) =>
      `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    img.src = svgDataUrl(
      `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="12" viewBox="0 0 28 12"><polyline points="0,10 8,2 16,6 24,0 28,4" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    );
    return img;
  };

  const marginPlugin = useMemo(
    () => ({
      id: 'table-margin-plugin',
      afterLayout: (chart: any) => {
        try {
          const left = chart.scales?.['y-left']?.width ?? 0;
          const right = chart.scales?.['y-right']?.width ?? 0;
          const chartInner = chart.chartArea?.width ?? 0;
          const cellWidth =
            weeks?.length > 0 ? Math.max(32, Number((chartInner / weeks.length).toFixed(2))) : 50;
          setTableMargins((prev) =>
            prev.left === left && prev.right === right && prev.cellWidth === cellWidth
              ? prev
              : { left, right, cellWidth }
          );
        } catch (e) {
          // swallow errors silently; measurement is best-effort
        }
      },
    }),
    []
  );

  return (
    <div style={{ width: '100%', padding: 12 }}>
      <div style={{ height: 420 }}>
        <Chart ref={chartRef} type="bar" data={data} options={options} plugins={[marginPlugin]} />
      </div>

      {/* Data table aligned to weeks */}
      <div style={{ marginTop: 12, marginRight: tableMargins.right }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {tableRows.map((row) => (
              <tr key={row.key}>
                <td
                  style={{
                    verticalAlign: 'middle',
                    border: '1px solid #e5e7eb',
                    width: tableMargins.left,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                    }}
                  >
                    {/* legend glyph */}
                    {row.shape === 'bar' ? (
                      <span
                        style={{
                          width: 24,
                          height: 12,
                          background: row.color,
                          display: 'inline-block',
                          borderRadius: 3,
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}
                      />
                    ) : (
                      <svg
                        width="28"
                        height="12"
                        viewBox="0 0 28 12"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <polyline
                          points="0,10 8,2 16,6 24,0 28,4"
                          fill="none"
                          stroke={row.color}
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {/* <div style={{ fontSize: 13 }}>{row.label}</div> */}
                  </div>
                </td>
                {row.values.map((value, i) => (
                  <td
                    key={i}
                    style={{
                      textAlign: 'center',
                      border: '1px solid #e5e7eb',
                      fontSize: 13,
                      width: tableMargins.cellWidth,
                      fontWeight: i === 6 ? 'bold' : '',
                      backgroundColor: i === 6 ? 'rgba(255, 255, 0, 0.85)' : '',
                    }}
                  >
                    {value !== null ? `${value.toFixed(1)}%` : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyProgressLineChart;
